import {
    type ComponentPropsWithRef,
    createContext,
    type MouseEvent,
    type PropsWithChildren,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";
import useInitializedContext from "@/hooks/use-initialized-context.ts";
import useOnClickOutside from "@/hooks/use-on-click-outside.ts";
import type { Nullable } from "@/types";
import { cn } from "@/utils/style.ts";
import Button, { type ButtonProps } from "./button.tsx";
import Icon from "./icon.tsx";

type DialogContextType = {
    open: boolean;
    onOpen: () => void;
    onClose: () => void;
};

const DialogContext = createContext<Nullable<DialogContextType>>(null);

const useDialog = () => useInitializedContext(DialogContext, "Dialog");

export type DialogProps = PropsWithChildren<{ open?: boolean; onOpenChange?: (open: boolean) => void }>;
export function Dialog({ children, open: controlledOpen = false, onOpenChange }: DialogProps) {
    const [open, setOpen] = useState<boolean>(controlledOpen);

    const handleOpenChange = (newOpen: boolean) => {
        setOpen(newOpen);
        onOpenChange?.(newOpen);
    };

    const onOpen = () => handleOpenChange(true);
    const onClose = () => handleOpenChange(false);

    useEffect(() => {
        if (typeof controlledOpen === "undefined") return;

        // eslint-disable-next-line react-hooks/set-state-in-effect
        setOpen(controlledOpen);
    }, [controlledOpen]);

    return <DialogContext value={{ open, onOpen, onClose }}>{children}</DialogContext>;
}

export type DialogTriggerProps = ButtonProps;
export function DialogTrigger({ children, onClick, ...props }: DialogTriggerProps) {
    const { onOpen } = useDialog();

    const handleClick = (evt: MouseEvent<HTMLButtonElement>) => {
        onOpen();
        onClick?.(evt);
    };

    return (
        <Button {...props} onClick={handleClick}>
            {children}
        </Button>
    );
}

export type DialogContentProps = ComponentPropsWithRef<"dialog"> & { closeOnClickOutside?: boolean };
export function DialogContent({ children, className, closeOnClickOutside = true, ...props }: DialogContentProps) {
    const { open, onClose } = useDialog();

    const ref = useOnClickOutside<HTMLDialogElement>(closeOnClickOutside ? onClose : () => undefined);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useImperativeHandle(props.ref, () => ref.current!);

    useEffect(() => {
        if (!ref.current) return;

        if (open) ref.current.showModal();
        else ref.current.close();
    }, [open, ref]);

    return (
        <dialog {...props} className={cn("dialog", className)} ref={ref}>
            {children}
            <DialogClose className="dialog__close" variant="icon">
                <Icon name="close" aria-label="Close modal" />
            </DialogClose>
        </dialog>
    );
}

export type DialogCloseProps = ButtonProps;
export function DialogClose({ children, onClick, ...props }: DialogCloseProps) {
    const { onClose } = useDialog();

    const handleClick = (evt: MouseEvent<HTMLButtonElement>) => {
        onClose();
        onClick?.(evt);
    };

    return (
        <Button {...props} onClick={handleClick}>
            {children}
        </Button>
    );
}

export type DialogHeaderProps = ComponentPropsWithRef<"header">;
export function DialogHeader({ children, className, ...props }: DialogHeaderProps) {
    return (
        <header {...props} className={cn("dialog__header", className)}>
            {children}
        </header>
    );
}

export type DialogTitleProps = ComponentPropsWithRef<"h2">;
export function DialogTitle({ children, className, ...props }: DialogTitleProps) {
    return (
        <h2 {...props} className={cn("dialog__title", className)}>
            {children}
        </h2>
    );
}

export type DialogDescriptionProps = ComponentPropsWithRef<"p">;
export function DialogDescription({ children, className, ...props }: DialogDescriptionProps) {
    return (
        <p {...props} className={cn("dialog__description", className)}>
            {children}
        </p>
    );
}

export type DialogBodyProps = ComponentPropsWithRef<"div">;
export function DialogBody({ children, className, ...props }: DialogBodyProps) {
    return (
        <div {...props} className={cn("dialog__body", className)}>
            {children}
        </div>
    );
}

export type DialogFooterProps = ComponentPropsWithRef<"footer">;
export function DialogFooter({ children, className, ...props }: DialogFooterProps) {
    return (
        <footer {...props} className={cn("dialog__footer", className)}>
            {children}
        </footer>
    );
}
